import { eventBus } from '../core/EventBus';
import { lerp } from '../core/Tween';
import { PlantSpecies, PlantStage, PlantStateConfig } from './PlantData';

export type PlantState = 'thriving' | 'lazy' | 'withering' | 'dormant';

export interface PlantPosition {
  x: number;
  y: number;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export class Plant {
  id: string;
  species: PlantSpecies;
  position: PlantPosition;
  currentStageIndex: number = 0;
  stageProgress: number = 0;
  displayProgress: number = 0;
  state: PlantState = 'thriving';

  health: number = 100;
  moisture: number;
  supplementalLight: number;
  wateringAmount: number = 15;

  debugStateOverride: boolean = false;

  visualState: {
    droopAngle: number;
    colorSaturation: number;
    swayAmplitude: number;
    browning: number;
    alpha: number;
  } = {
    droopAngle: 0,
    colorSaturation: 1.0,
    swayAmplitude: 2,
    browning: 0,
    alpha: 1.0
  };

  private targetState: PlantStateConfig;
  private history: Array<{ timestamp: number; event: string; detail?: any }> = [];

  private unsubUpdate: () => void;

  constructor(species: PlantSpecies, position?: PlantPosition) {
    this.id = generateId();
    this.species = species;
    this.position = position ?? { x: 0.5, y: 0.7 };
    this.moisture = species.ecology.moisture.optimal;
    this.supplementalLight = species.ecology.light.optimal;
    this.targetState = species.states.thriving;
    this.logEvent('planted');

    this.unsubUpdate = eventBus.on('game:update', this.update.bind(this));
  }

  private update(deltaTime: number, _gameTime: number): void {
    const totalStress = this.calculateTotalStress();

    if (totalStress > 0) {
      this.health -= totalStress * 0.05 * deltaTime;
    } else {
      this.health += 0.08 * deltaTime;
    }
    this.health = Math.max(0, Math.min(100, this.health));

    this.updateStateFromHealth();

    const currentStage = this.getCurrentStage();
    if (currentStage && currentStage.duration !== Infinity) {
      const growthMultiplier = 0.3 + (this.health / 100) * 0.7;
      this.stageProgress += (deltaTime / currentStage.duration) * growthMultiplier;
      if (this.stageProgress >= 1) {
        this.advanceStage();
      }
    }
  }

  updateDisplay(): void {
    if (Math.abs(this.stageProgress - this.displayProgress) > 0.3) {
      this.displayProgress = this.stageProgress;
    } else {
      this.displayProgress = lerp(this.displayProgress, this.stageProgress, 0.08);
    }

    if (!this.debugStateOverride) {
      this.interpolateVisualState();
    }
  }

  private advanceStage(): void {
    if (this.currentStageIndex < this.species.stages.length - 1) {
      this.currentStageIndex++;
      this.stageProgress = 0;
      this.displayProgress = 0;
      this.logEvent('stage_changed', {
        stage: this.species.stages[this.currentStageIndex].id
      });
      eventBus.emit('plant:stageChanged', this, this.currentStageIndex);
    }
  }

  private calculateTotalStress(): number {
    const eco = this.species.ecology;
    const lightStress = this.calculateCareStress(this.supplementalLight, eco.light.optimal);
    const moistureStress = this.calculateCareStress(this.moisture, eco.moisture.optimal);
    const tempStress = this.calculateTempStress(this.temperature, eco.temperature.optimal, eco.temperature.range);
    return (lightStress + moistureStress + tempStress) / 3;
  }

  private updateStateFromHealth(): void {
    if (this.debugStateOverride) return;

    const stateOrder: PlantState[] = ['thriving', 'lazy', 'withering', 'dormant'];
    const enterThresholds = [70, 45, 20];
    const exitThresholds = [75, 50, 25];

    const currentIndex = stateOrder.indexOf(this.state);
    let newState = this.state;

    for (let i = enterThresholds.length - 1; i >= 0; i--) {
      if (this.health <= enterThresholds[i] && currentIndex <= i) {
        newState = stateOrder[i + 1];
        break;
      }
    }

    if (newState === this.state && currentIndex > 0) {
      if (this.health >= exitThresholds[currentIndex - 1]) {
        newState = stateOrder[currentIndex - 1];
      }
    }

    if (newState !== this.state) {
      this.state = newState;
      this.targetState = this.species.states[newState];
      this.logEvent('state_changed', { state: newState });
      eventBus.emit('plant:stateChanged', this, newState);
    }
  }

  private calculateCareStress(value: number, minComfortable: number): number {
    if (value >= minComfortable) return 0;
    return (minComfortable - value) / minComfortable;
  }

  private calculateTempStress(value: number, _optimal: number, range: [number, number]): number {
    const [min, max] = range;
    if (value >= min && value <= max) return 0;
    if (value < min) return (min - value) / min;
    return Math.min(1, (value - max) / 10);
  }

  private interpolateVisualState(): void {
    const speed = 0.08;
    this.visualState.droopAngle = lerp(this.visualState.droopAngle, this.targetState.droopAngle, speed);
    this.visualState.colorSaturation = lerp(this.visualState.colorSaturation, this.targetState.colorSaturation, speed);
    this.visualState.swayAmplitude = lerp(this.visualState.swayAmplitude, this.targetState.swayAmplitude, speed);
    this.visualState.browning = lerp(this.visualState.browning, this.targetState.browning, speed);
    this.visualState.alpha = lerp(this.visualState.alpha, this.targetState.alpha, speed);
  }

  getCurrentStage(): PlantStage | null {
    return this.species.stages[this.currentStageIndex] || null;
  }

  getStressLevels(): { light: number; moisture: number; temperature: number } {
    const eco = this.species.ecology;
    return {
      light: this.calculateCareStress(this.supplementalLight, eco.light.optimal),
      moisture: this.calculateCareStress(this.moisture, eco.moisture.optimal),
      temperature: this.calculateTempStress(this.temperature, eco.temperature.optimal, eco.temperature.range)
    };
  }

  getVisualState() {
    return this.visualState;
  }

  water(amount: number): void {
    this.moisture = Math.min(100, this.moisture + amount);
    this.logEvent('watered', { amount });
    eventBus.emit('plant:watered', this, amount);
  }

  setMoisture(level: number): void {
    this.moisture = Math.max(0, Math.min(100, level));
    this.logEvent('moisture_set', { level });
  }

  setSupplementalLight(level: number): void {
    this.supplementalLight = Math.max(0, Math.min(100, level));
    this.logEvent('light_set', { level });
  }

  toggleSupplementalLight(): void {
    this.supplementalLight = this.supplementalLight > 50 ? 0 : 100;
    this.logEvent('light_toggled', { level: this.supplementalLight });
  }

  adjustLight(level: number): void {
    this.supplementalLight = Math.max(0, Math.min(100, level));
    this.logEvent('light_adjusted', { level });
  }

  get temperature(): number {
    return this._temperature;
  }

  set temperature(value: number) {
    this._temperature = value;
  }

  private _temperature: number = 22;

  private logEvent(event: string, detail?: any): void {
    this.history.push({ timestamp: Date.now(), event, detail });
    if (this.history.length > 50) {
      this.history.shift();
    }
  }

  getHistory() {
    return [...this.history];
  }

  setStage(index: number): void {
    const clamped = Math.max(0, Math.min(index, this.species.stages.length - 1));
    const oldIndex = this.currentStageIndex;
    this.currentStageIndex = clamped;
    this.stageProgress = 0;
    this.displayProgress = 0;
    if (oldIndex !== clamped) {
      this.logEvent('stage_changed', { stage: this.species.stages[clamped].id, debug: true });
      eventBus.emit('plant:stageChanged', this, clamped);
    }
  }

  setStageProgress(progress: number): void {
    this.stageProgress = Math.max(0, Math.min(1, progress));
  }

  setStateDirectly(state: PlantState): void {
    if (state !== this.state) {
      this.state = state;
      this.targetState = this.species.states[state];
      this.logEvent('state_changed', { state, debug: true });
      eventBus.emit('plant:stateChanged', this, state);
    }
  }

  overrideVisualState(params: {
    droopAngle?: number;
    colorSaturation?: number;
    swayAmplitude?: number;
    browning?: number;
    alpha?: number;
  }): void {
    if (params.droopAngle !== undefined) this.visualState.droopAngle = params.droopAngle;
    if (params.colorSaturation !== undefined) this.visualState.colorSaturation = params.colorSaturation;
    if (params.swayAmplitude !== undefined) this.visualState.swayAmplitude = params.swayAmplitude;
    if (params.browning !== undefined) this.visualState.browning = params.browning;
    if (params.alpha !== undefined) this.visualState.alpha = params.alpha;
  }

  resetVisualState(): void {
    this.targetState = this.species.states[this.state];
    this.visualState.droopAngle = this.targetState.droopAngle;
    this.visualState.colorSaturation = this.targetState.colorSaturation;
    this.visualState.swayAmplitude = this.targetState.swayAmplitude;
    this.visualState.browning = this.targetState.browning;
    this.visualState.alpha = this.targetState.alpha;
  }

  destroy(): void {
    this.unsubUpdate();
  }

  toJSON() {
    return {
      id: this.id,
      species: this.species.id,
      position: { ...this.position },
      currentStage: this.getCurrentStage()?.id || 'seed',
      stageProgress: this.stageProgress,
      state: this.state,
      health: this.health,
      moisture: this.moisture,
      supplementalLight: this.supplementalLight,
      wateringAmount: this.wateringAmount,
      temperature: this._temperature,
      history: this.getHistory()
    };
  }

  static fromJSON(data: any, species: PlantSpecies): Plant {
    const plant = new Plant(species, data.position);
    plant.id = data.id || generateId();
    const stageIndex = species.stages.findIndex(s => s.id === data.currentStage);
    plant.currentStageIndex = Math.max(0, stageIndex);
    plant.stageProgress = data.stageProgress || 0;
    plant.displayProgress = plant.stageProgress;
    plant.state = (data.state as PlantState) || 'thriving';
    plant.health = data.health ?? 100;
    plant.moisture = data.moisture ?? species.ecology.moisture.optimal;
    plant.supplementalLight = data.supplementalLight ?? species.ecology.light.optimal;
    plant.wateringAmount = data.wateringAmount ?? 15;
    plant._temperature = data.temperature ?? species.ecology.temperature.optimal;
    if (data.history) {
      plant.history = [...data.history];
    }
    return plant;
  }
}
